import { DEFAULT_APPEARANCE, appearanceAttributes, type Appearance } from '@velora/tokens';
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/** Applies an appearance to an element (default <html>), removing unset attributes. */
export function applyAppearance(a: Appearance, el: HTMLElement = document.documentElement): void {
  for (const [name, value] of Object.entries(appearanceAttributes(a))) {
    if (value === undefined) el.removeAttribute(name);
    else el.setAttribute(name, value);
  }
}

interface AppearanceContextValue {
  appearance: Appearance;
  setAppearance: (next: Partial<Appearance>) => void;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

export interface AppearanceProviderProps {
  /** Controlled appearance. Omit to let the provider own state. */
  appearance?: Appearance;
  defaultAppearance?: Partial<Appearance>;
  onAppearanceChange?: (next: Appearance) => void;
  /**
   * `document` (default) writes the data attributes to <html>, so portalled
   * overlays (dialogs, menus, toasts) follow the theme. `scope` writes them to
   * a wrapper element instead; use it only for side-by-side previews, since
   * portalled content then still follows the document theme.
   */
  target?: 'document' | 'scope';
  children: ReactNode;
}

/**
 * Applies the display appearance (theme, contrast, density, clinical preset,
 * reduced motion, performance mode). Persisting user and device preferences
 * belongs to the app shell; this provider only applies them.
 */
export function AppearanceProvider({
  appearance,
  defaultAppearance,
  onAppearanceChange,
  target = 'document',
  children,
}: AppearanceProviderProps) {
  const [inner, setInner] = useState<Appearance>({ ...DEFAULT_APPEARANCE, ...defaultAppearance });
  const current = appearance ?? inner;

  const setAppearance = useCallback(
    (next: Partial<Appearance>) => {
      const merged = { ...current, ...next };
      if (!appearance) setInner(merged);
      onAppearanceChange?.(merged);
    },
    [appearance, current, onAppearanceChange],
  );

  useLayoutEffect(() => {
    if (target === 'document') applyAppearance(current);
  }, [current, target]);

  const value = useMemo(() => ({ appearance: current, setAppearance }), [current, setAppearance]);

  return (
    <AppearanceContext.Provider value={value}>
      {target === 'scope' ? (
        <div className="vel-appearance-scope" {...appearanceAttributes(current)}>
          {children}
        </div>
      ) : (
        children
      )}
    </AppearanceContext.Provider>
  );
}

export function useAppearance(): AppearanceContextValue {
  const ctx = useContext(AppearanceContext);
  if (!ctx) throw new Error('useAppearance must be used inside <AppearanceProvider>');
  return ctx;
}
